import { accessToken, login } from '@/auth/oidc'
import { ApiError, request } from '@/lib/api'
import type {
  AcquiringWebhookRow,
  BankAccount,
  Organization,
  ConversionResult,
  CustomerBalance,
  FiscalReceipt,
  ImportResult,
  LedgerEntry,
  LedgerTotal,
  PaymentBrief,
  Period,
  StatementImport,
  StatementLine,
} from './types'

/**
 * Клиент API раздела «Оплата» (ТЗ §4.4) — тикет #78.
 *
 * Никакой логики разнесения здесь нет и быть не должно: автосверка, идемпотентность
 * импорта, порядок закрытия счетов — всё на сервере и покрыто его тестами. Клиент
 * показывает то, что ему отдали.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

// ------------------------------------------------------------------ организации

/** Юридические лица: чью кассу и чьи счета показывать. */
export function fetchOrganizations(signal?: AbortSignal): Promise<Organization[]> {
  return request<Organization[]>('/payments/organizations', { signal })
}

// ------------------------------------------------------------------ банковские счета

export function fetchBankAccounts(
  organizationId: string,
  signal?: AbortSignal,
): Promise<BankAccount[]> {
  return request<BankAccount[]>(
    `/payments/bank-accounts?organizationId=${encodeURIComponent(organizationId)}`,
    { signal },
  )
}

// ------------------------------------------------------------------ выписка

/**
 * Загрузка файла 1С «Клиент-Банк».
 *
 * Собственный `fetch`, а не общий `request`: тело — это содержимое файла как есть,
 * текстом, а `request` заворачивает любое тело в JSON. Отправить выписку строкой внутри
 * JSON-значения означало бы экранировать её переводы строк и разбирать обратно на
 * сервере — то есть завести собственную кодировку там, где формат уже есть.
 *
 * Файл читается в UTF-8. Экспорт 1С часто бывает в CP1251, и это **не** обрабатывается —
 * пробел назван вслух: перекодировка на клиенте угадывала бы кодировку по содержимому, а
 * ошибка угадывания портит имена плательщиков молча. Правильное место для этого —
 * сервер, где кодировка объявлена в шапке файла (`Кодировка=Windows`).
 */
export async function importStatement(
  bankAccountId: string,
  content: string,
  fileName?: string,
): Promise<ImportResult> {
  const token = await accessToken()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'text/plain; charset=utf-8',
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(
    `${BASE_URL}/payments/statements/${encodeURIComponent(bankAccountId)}/import`
      + (fileName ? `?fileName=${encodeURIComponent(fileName)}` : ''),
    { method: 'POST', headers, body: content },
  )

  if (response.status === 401) {
    await login()
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    throw new ApiError(
      response.status,
      problem?.code ?? null,
      problem?.detail ?? problem?.message ?? response.statusText,
    )
  }

  return (await response.json()) as ImportResult
}

/** История загрузок выписки по счёту, новые первыми. */
export function fetchStatementImports(
  bankAccountId: string,
  signal?: AbortSignal,
): Promise<StatementImport[]> {
  return request<StatementImport[]>(
    `/payments/statements/${encodeURIComponent(bankAccountId)}/imports`,
    { signal },
  )
}

/** Одна загрузка и строки, которые она принесла. */
export function fetchStatementImport(importId: string, signal?: AbortSignal): Promise<StatementImport> {
  return request<StatementImport>(`/payments/statements/imports/${encodeURIComponent(importId)}`, { signal })
}

/** Строки, которые банк прислал, а мы ещё не провели. */
export function fetchUnprocessedLines(
  bankAccountId: string,
  signal?: AbortSignal,
): Promise<StatementLine[]> {
  return request<StatementLine[]>(
    `/payments/statements/${encodeURIComponent(bankAccountId)}/unprocessed`,
    { signal },
  )
}

export function convertLine(lineId: string): Promise<PaymentBrief> {
  return request<PaymentBrief>(
    `/payments/statements/lines/${encodeURIComponent(lineId)}/convert`,
    { method: 'POST' },
  )
}

export function convertAll(bankAccountId: string): Promise<ConversionResult> {
  return request<ConversionResult>(
    `/payments/statements/${encodeURIComponent(bankAccountId)}/convert-all`,
    { method: 'POST' },
  )
}

export function absorbAdvances(bankAccountId: string): Promise<{ absorbed: string }> {
  return request<{ absorbed: string }>(
    `/payments/statements/${encodeURIComponent(bankAccountId)}/absorb-advances`,
    { method: 'POST' },
  )
}

// ------------------------------------------------------------------ очередь сверки

/**
 * Платежи без опознанного контрагента.
 *
 * Пустой ответ здесь значит «всё разнесено», а не «список не написан», — и отличить одно
 * от другого можно только тем, что экран этот список показывает.
 */
export function fetchNeedingAttention(
  organizationId: string,
  signal?: AbortSignal,
): Promise<PaymentBrief[]> {
  return request<PaymentBrief[]>(
    `/payments/needing-attention?organizationId=${encodeURIComponent(organizationId)}`,
    { signal },
  )
}

export function fetchBalances(
  counterpartyId: string,
  signal?: AbortSignal,
): Promise<CustomerBalance[]> {
  return request<CustomerBalance[]>(
    `/payments/counterparties/${encodeURIComponent(counterpartyId)}/balances`,
    { signal },
  )
}

// ------------------------------------------------------------------ касса

export function fetchLedger(
  organizationId: string,
  period: Period,
  signal?: AbortSignal,
): Promise<LedgerEntry[]> {
  return request<LedgerEntry[]>(
    `/payments/ledger?organizationId=${encodeURIComponent(organizationId)}` +
      `&from=${period.from}&to=${period.to}`,
    { signal },
  )
}

export function fetchLedgerTotals(
  organizationId: string,
  currency: string,
  period: Period,
  signal?: AbortSignal,
): Promise<LedgerTotal[]> {
  return request<LedgerTotal[]>(
    `/payments/ledger/totals?organizationId=${encodeURIComponent(organizationId)}` +
      `&currency=${encodeURIComponent(currency)}&from=${period.from}&to=${period.to}`,
    { signal },
  )
}

/**
 * Остаток кассы за период.
 *
 * Отдельным запросом, а не суммой видимых строк: остаток — свойство периода, и
 * посчитанный клиентом он разойдётся с серверным, как только список станет постраничным.
 */
export function fetchLedgerBalance(
  organizationId: string,
  currency: string,
  period: Period,
  signal?: AbortSignal,
): Promise<{ currency: string; netFlow: string }> {
  return request<{ currency: string; netFlow: string }>(
    `/payments/ledger/balance?organizationId=${encodeURIComponent(organizationId)}` +
      `&currency=${encodeURIComponent(currency)}&from=${period.from}&to=${period.to}`,
    { signal },
  )
}

/** Ручная проводка кассы: то, что в кассе произошло без платежа (`POST /payments/ledger`). */
export interface ManualEntry {
  organizationId: string
  date: string
  item: string
  direction: 'incoming' | 'outgoing'
  amount: string
  currency: string
  description: string
}

/**
 * Записать приход или расход кассы.
 *
 * Описание обязательно — его требует сервер (`ledger.no_description`): необъяснённая ручная
 * проводка — единственное, чего журнал не переваривает. Сумма уходит строкой, как её ввёл
 * человек: разбор в double на клиенте вернул бы копейки, которых он не вводил.
 */
export function recordManualEntry(entry: ManualEntry): Promise<{ entryId: number }> {
  return request<{ entryId: number }>('/payments/ledger', { method: 'POST', body: entry })
}

/**
 * Сторнирование проводки.
 *
 * Причина обязательна — её требует сервер. Актор в тело не кладётся и положен быть не
 * может: кто сторнировал, решает сервер по токену, иначе след стирается тем, что клиент
 * назовёт себя кем угодно.
 */
export function reverseLedgerEntry(
  entryId: number,
  reason: string,
): Promise<{ entryId: number; reverses: number }> {
  return request<{ entryId: number; reverses: number }>(`/payments/ledger/${entryId}/reverse`, {
    method: 'POST',
    body: { reason },
  })
}

// ------------------------------------------------------------------ фискальные чеки

/** Чеки, не доехавшие до ОФД, — включая те, чьи попытки исчерпаны. */
export function fetchOutstandingReceipts(signal?: AbortSignal): Promise<FiscalReceipt[]> {
  return request<FiscalReceipt[]>('/payments/receipts/outstanding', { signal })
}

export function retryReceipts(batchSize = 50): Promise<{ submitted: number }> {
  return request<{ submitted: number }>(`/payments/receipts/retry?batchSize=${batchSize}`, {
    method: 'POST',
  })
}

/**
 * Повтор одного чека — по правилам обхода: зарегистрированный возвращается как есть,
 * несвоевременный отвергается (`fiscal.retry_not_due`), брошенный повторяется один раз.
 * Удалась ли попытка, говорит `status` возвращённого чека, а не код ответа.
 */
export function retryReceipt(receiptId: string): Promise<FiscalReceipt> {
  return request<FiscalReceipt>(`/payments/receipts/${receiptId}/retry`, { method: 'POST' })
}

// ------------------------------------------------------------------ эквайринг

export function fetchUnprocessedWebhooks(
  organizationId: string,
  signal?: AbortSignal,
): Promise<AcquiringWebhookRow[]> {
  return request<AcquiringWebhookRow[]>(
    `/payments/acquiring/unprocessed?organizationId=${encodeURIComponent(organizationId)}`,
    { signal },
  )
}
